import React, { useContext, useMemo } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import { format } from 'date-fns';
import { 
  BuildingOfficeIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  UserGroupIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const formatCurrency = (number) => {
  return `£${formatNumber(number)}`;
};

function PerformanceSnapshots() {
  const { uinfo } = useContext(GlobalContext);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  // Fetch performance data
  const { data: performanceData, isLoading: perfLoading } = useApi({
    url: '/visiondashboard/operatorPerformance',
    initial: []
  });

  const { data: branches, isLoading: branchesLoading } = useApi({
    url: '/salesassist/cplusassist/branches',
    initial: []
  });

  const { data: currentMonthSalesData, isLoading: salesLoading } = useApi({
    url: `/visiondashboard/salesassist/getSales/${currentYear}/${currentMonth}/`,
    initial: []
  });

  // Process performance data
  const snapshots = useMemo(() => {
    if (!performanceData || !Array.isArray(performanceData) || performanceData.length === 0) {
      return {
        branchPerformance: [],
        topReps: [],
        bottomReps: [],
        salesVsTarget: null
      };
    }

    // Branch Performance Summary
    const branchMap = {};
    performanceData.forEach(operator => {
      const branch = operator.OpBranch || operator.Branch || 'Unknown';
      if (!branchMap[branch]) {
        branchMap[branch] = {
          branch,
          totalSales: 0,
          totalMargin: 0,
          repCount: 0,
          avgSalesPerRep: 0
        };
      }
      branchMap[branch].totalSales += parseFloat(operator.invoiceValueMonth || 0);
      branchMap[branch].totalMargin += parseFloat(operator.marginMonth || 0);
      branchMap[branch].repCount++;
    });

    const branchPerformance = Object.values(branchMap)
      .map(branch => ({
        ...branch,
        avgSalesPerRep: branch.repCount > 0 ? branch.totalSales / branch.repCount : 0,
        marginPercent: branch.totalSales > 0 ? (branch.totalMargin / branch.totalSales) * 100 : 0
      }))
      .sort((a, b) => b.totalSales - a.totalSales);

    // Top/Bottom Reps
    const reps = performanceData
      .map(operator => ({
        name: operator.OperatorName || operator.Name || 'Unknown',
        inits: operator._id || operator.Inits || 'Unknown',
        branch: operator.OpBranch || operator.Branch || 'Unknown',
        sales: parseFloat(operator.invoiceValueMonth || 0),
        margin: parseFloat(operator.marginMonth || 0),
        marginPercent: operator.invoiceValueMonth > 0 
          ? (parseFloat(operator.marginMonth || 0) / parseFloat(operator.invoiceValueMonth || 0)) * 100 
          : 0
      }))
      .filter(rep => rep.sales > 0)
      .sort((a, b) => b.sales - a.sales);

    const topReps = reps.slice(0, 5);
    const bottomReps = reps.slice(-5).reverse();

    // Sales vs Target
    let totalSales = 0;
    let totalTarget = 0;
    performanceData.forEach(operator => {
      totalSales += parseFloat(operator.invoiceValueMonth || 0);
      totalTarget += parseFloat(operator.SalesTarget || 0);
    });

    const salesVsTarget = {
      totalSales,
      totalTarget,
      percentAchieved: totalTarget > 0 ? (totalSales / totalTarget) * 100 : 0,
      variance: totalSales - totalTarget
    };

    return {
      branchPerformance,
      topReps,
      bottomReps,
      salesVsTarget
    };
  }, [performanceData]);

  const isLoading = perfLoading || branchesLoading || salesLoading;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sales vs Target Summary */}
      {snapshots.salesVsTarget && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales vs Target (Current Month)</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Sales</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(snapshots.salesVsTarget.totalSales)}
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Target</div>
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(snapshots.salesVsTarget.totalTarget)}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Achievement %</div>
              <div className="text-2xl font-bold text-green-600">
                {snapshots.salesVsTarget.percentAchieved.toFixed(1)}%
              </div>
            </div>
            <div className={clsx(
              'rounded-lg p-4',
              snapshots.salesVsTarget.variance >= 0 ? 'bg-green-50' : 'bg-red-50'
            )}>
              <div className="text-sm text-gray-600 mb-1">Variance</div>
              <div className={clsx(
                'text-2xl font-bold flex items-center space-x-1',
                snapshots.salesVsTarget.variance >= 0 ? 'text-green-600' : 'text-red-600'
              )}>
                {snapshots.salesVsTarget.variance >= 0 ? (
                  <ArrowUpTrayIcon className="w-5 h-5" />
                ) : (
                  <ArrowDownTrayIcon className="w-5 h-5" />
                )}
                <span>{formatCurrency(Math.abs(snapshots.salesVsTarget.variance))}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Branch Performance */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
          <span>Branch Performance Summary</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branch
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Sales
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Margin
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margin %
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reps
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Sales/Rep
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {snapshots.branchPerformance.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No branch performance data available
                  </td>
                </tr>
              ) : (
                snapshots.branchPerformance.map((branch, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {branch.branch}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {formatCurrency(branch.totalSales)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(branch.totalMargin)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-semibold',
                        branch.marginPercent >= 20 ? 'bg-green-100 text-green-800' :
                        branch.marginPercent >= 10 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      )}>
                        {branch.marginPercent.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500">
                      {branch.repCount}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                      {formatCurrency(branch.avgSalesPerRep)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top/Bottom Reps */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Reps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
            <span>Top 5 Performing Reps</span>
          </h3>
          {snapshots.topReps.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No data available</div>
          ) : (
            <div className="space-y-3">
              {snapshots.topReps.map((rep, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{rep.name}</div>
                      <div className="text-xs text-gray-500">{rep.branch}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">{formatCurrency(rep.sales)}</div>
                    <div className="text-xs text-gray-500">{rep.marginPercent.toFixed(1)}% margin</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Reps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />
            <span>Bottom 5 Performing Reps</span>
          </h3>
          {snapshots.bottomReps.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No data available</div>
          ) : (
            <div className="space-y-3">
              {snapshots.bottomReps.map((rep, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">
                      {snapshots.bottomReps.length - idx}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{rep.name}</div>
                      <div className="text-xs text-gray-500">{rep.branch}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-red-600">{formatCurrency(rep.sales)}</div>
                    <div className="text-xs text-gray-500">{rep.marginPercent.toFixed(1)}% margin</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PerformanceSnapshots;

